/* hys-cart-tier-nudge.js
 * Behaviour for the Crew Pack tier-progress nudge in the cart drawer
 * (snippets/hys-cart-tier-nudge.liquid).
 *
 * Subscribes to the global Cart store (same reactive pattern as
 * cart-free-shipping-bar.js — NOT the render-once pattern of
 * hys-cart-hardcase-upsell) so the message updates live as quantities
 * change, without waiting for a drawer re-open.
 *
 * Visual: single slim row (Peter's mockup 2026-07-19, replaced the
 * milestone stage cards) — tag icon + message left, muted further-tier
 * hint ("3+ → 15%") right. Still deliberately not a progress bar, so it
 * reads as a different thing than the free-shipping bar directly above it.
 *
 * DISPLAY-ONLY CONTRACT: the real discount is the Crew Pack Bundle
 * automatic discount (Shopify Function). This module never sends prices
 * or discounts to the cart — it only reads state and renders copy.
 *
 * Eligibility mirror (must match the Function; see the snippet comment):
 *   product in bundle collection OR line already carries a Crew Pack
 *   discount; $0 gift lines excluded; ANSI-Only lines excluded only while
 *   their product carries the ansi-only-clearance tag (clearanceProductIds).
 *
 * The ANSI-Only check below is one copy of the Function's rule (tag AND
 * title). Full list of copies: snippets/hys-cart-tier-nudge.liquid.
 */
import Cart from 'cart-store';
import { formatMoney } from 'utils';

class HysTierNudge extends HTMLElement {
  connectedCallback() {
    const cfgEl = this.querySelector('[data-tier-config]');
    let cfg = null;
    try {
      cfg = JSON.parse(cfgEl ? cfgEl.textContent : 'null');
    } catch (e) {
      cfg = null;
    }
    if (!cfg) return;

    /* Extra-pair offer (2026-10-21, replaces the Crew Pack tiers): "add up
     * to 3 more pairs at 20% off". The real discounts are three automatic
     * Buy X Get Y discounts; extraPair.productIds mirrors their product list
     * (snippets/hys-extra-pair-state.liquid). When it is off, everything
     * below runs exactly as before. */
    const ep = cfg.extraPair || null;
    this.extra = ep && ep.on === true && Array.isArray(ep.productIds) && ep.productIds.length
      ? { pct: Number(ep.percent) || 20, max: Number(ep.maxExtra) || 3, ids: new Set(ep.productIds.map(Number)) }
      : null;

    this.tiers = (Array.isArray(cfg.tiers) ? cfg.tiers : [])
      .filter((t) => t && typeof t.min_qty === 'number' && typeof t.value === 'number')
      .sort((a, b) => a.min_qty - b.min_qty);
    if (!this.extra && !this.tiers.length) return;

    this.eligibleIds = new Set(cfg.eligibleProductIds || []);
    this.clearanceIds = new Set(cfg.clearanceProductIds || []);
    this.msgEl = this.querySelector('[data-message]');
    this.hintEl = this.querySelector('[data-hint]');

    this.unsubscribe = Cart.subscribe((state) => this.#update(state.cart));
    this.#update(Cart.getState().cart);

    /* Compact-on-scroll: once the customer scrolls the product list, the
     * whole header region (free-shipping bar + this nudge) shrinks so the
     * line items get the vertical space back — matters most on mobile where
     * the drawer height is tight. Class goes on cart-full so the CSS can
     * reach the sibling free-shipping bar too. Hysteresis (30/8) avoids
     * flicker at the boundary. The drawer's scroll container is
     * .cart--draw .cart__contents; on the cart PAGE cart-contents doesn't
     * scroll (scrollTop stays 0) so this never triggers there. */
    this.wrapperEl = this.closest('cart-full');
    this.scrollerEl = this.wrapperEl ? this.wrapperEl.querySelector('cart-contents') : null;
    if (this.wrapperEl && this.scrollerEl) {
      this.onScroll = () => {
        const top = this.scrollerEl.scrollTop;
        /* Overflow gate (Peter bug 2026-07-19): compacting returns ~100px+
         * to the list, so on a cart whose overflow is SMALLER than that,
         * compact-mode cancels the very overflow being scrolled into — the
         * list snaps back, the header re-expands, and scrolling feels
         * completely stuck. Only compact when there's clearly more content
         * than the compaction gives back. */
        const overflow = this.scrollerEl.scrollHeight - this.scrollerEl.clientHeight;
        if (top > 30 && overflow > 180) {
          this.wrapperEl.classList.add('hys-compact');
        } else if (top < 8) {
          this.wrapperEl.classList.remove('hys-compact');
        }
      };
      this.scrollerEl.addEventListener('scroll', this.onScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.scrollerEl && this.onScroll) this.scrollerEl.removeEventListener('scroll', this.onScroll);
  }

  /* Count eligible units + sum the discount already applied to them.
   * `saved` uses total_discount on eligible lines: those lines carry no
   * other line-level promos (ANSI clearance lines are excluded above). */
  #tally(cart) {
    let units = 0;
    let saved = 0;
    if (this.extra) {
      /* Extra-pair offer: whole pairs from the offer's product list count;
       * $0 gift lines never count. `saved` is every discount Shopify put on
       * those lines (normally one of the three 2nd-pair discounts). */
      (cart.items || []).forEach((item) => {
        if (item.price === 0 || item.final_price === 0) return;
        if (!this.extra.ids.has(Number(item.product_id))) return;
        units += item.quantity;
        saved += item.total_discount || 0;
      });
      return { units, saved };
    }
    (cart.items || []).forEach((item) => {
      // Same test as the Function: title starts "ANSI-Only" AND product is
      // clearance-tagged. Untagged ANSI-Only pairs count toward the tiers.
      if (/^ansi-only/i.test(item.variant_title || '') && this.clearanceIds.has(item.product_id)) return;
      if (item.final_price === 0) return;
      const inCollection = this.eligibleIds.has(item.product_id);
      // cart.js exposes only a discount's title, so this is a name match, not
      // proof of eligibility. It relies on the automatic discount's title
      // containing "Crew Pack"; renaming that discount silently breaks it.
      const hasCrewDiscount = (item.discounts || []).some((d) => /crew pack/i.test(d.title || ''));
      if (inCollection || hasCrewDiscount) {
        units += item.quantity;
        saved += item.total_discount || 0;
      }
    });
    return { units, saved };
  }

  #money(cents) {
    try {
      return formatMoney(cents, window.theme.money_format).replace(/[.,]00$/, '');
    } catch (e) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  }

  #update(cart) {
    if (!cart || !this.msgEl) return;
    const { units, saved } = this.#tally(cart);

    if (!cart.item_count || units === 0) {
      this.setAttribute('hidden', '');
      return;
    }
    this.removeAttribute('hidden');

    if (this.extra) {
      this.#renderExtra(units, saved);
      return;
    }

    const current = [...this.tiers].reverse().find((t) => units >= t.min_qty) || null;
    const next = this.tiers.find((t) => units < t.min_qty) || null;

    /* Copy per Peter's mockup 2026-07-19: "1 more pair unlocks 10% off",
     * bold dark action anchor + bold gold percentage, no emoji (the tag
     * icon in the snippet carries the visual). Still imperative, still
     * next-tier-only. */
    let html = '';
    const gold = (t) => `<strong class="hys-tier-nudge__gold">${t}</strong>`;
    if (!current && next) {
      const need = next.min_qty - units;
      html = `<strong>${need} more ${need === 1 ? 'pair' : 'pairs'}</strong> unlocks ${gold(`${next.value}% off`)}`;
    } else if (current && next) {
      const need = next.min_qty - units;
      html = `${gold(`${current.value}% off`)} unlocked — <strong>add ${need} more</strong> for ${gold(`${next.value}% off`)}`;
    } else if (current) {
      html = `${gold(`${current.value}% off`)} applied${saved > 0 ? ` — saving <strong>${this.#money(saved)}</strong>` : ' — max savings'}`;
    }
    this.msgEl.innerHTML = html;

    /* Right-side hint: the tiers BEYOND the immediate next one, compressed
     * to "3+ → 15%" (mockup). Hidden once there's nothing further to tease
     * — the message itself already names the next tier. */
    if (this.hintEl) {
      const later = next ? this.tiers.filter((t) => t.min_qty > next.min_qty) : [];
      const last = this.tiers[this.tiers.length - 1];
      const hint = later
        .map((t) => `${t.min_qty}${t === last ? '+' : ''} → ${t.value}%`)
        .join(' · ');
      this.hintEl.textContent = hint;
      this.hintEl.toggleAttribute('hidden', hint === '');
    }
  }

  /* Extra-pair states (Peter 2026-10-21). Up to `max` pairs after the first
   * get `pct`% off (cheapest first, applied by Shopify); pairs beyond that
   * are regular price, so the nudge stops asking once the cap is reached.
   *   1 pair      "Add a 2nd pair for 20% off"            hint "Up to 3 extra pairs"
   *   2..max      "Saving $X. Add a 3rd pair for 20% off"
   *   max+1 or more  "Max saving applied: $X off"
   * No em dashes in customer copy. */
  #renderExtra(units, saved) {
    const { pct, max } = this.extra;
    const gold = (t) => `<strong class="hys-tier-nudge__gold">${t}</strong>`;
    const ordinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    // money_format is '<span class=money>${{amount}} </span>' (space inside the span), so use plain text here
    const savedMoney = this.#money(saved).replace(/<[^>]*>/g, '').trim();
    const savedTxt = saved > 0 ? `Saving <strong>${savedMoney}</strong>. ` : '';
    let html = '';
    if (units <= max) {
      html = `${savedTxt}<strong>Add a ${ordinal(units + 1)} pair</strong> for ${gold(`${pct}% off`)}`;
    } else {
      html = saved > 0
        ? `${gold('Max saving')} applied: <strong>${savedMoney}</strong> off`
        : `${gold(`${pct}% off`)} on up to ${max} extra pairs`;
    }
    this.msgEl.innerHTML = html;
    if (this.hintEl) {
      const hint = units === 1 ? `Up to ${max} extra pairs` : '';
      this.hintEl.textContent = hint;
      this.hintEl.toggleAttribute('hidden', hint === '');
    }
  }
}

if (!customElements.get('hys-tier-nudge')) {
  customElements.define('hys-tier-nudge', HysTierNudge);
}
